import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Checkbox,
  FormControlLabel,
  ListSubheader,
} from '@mui/material';
import {
  Print as PrintIcon,
  Download as DownloadIcon,
  LocalShipping as ShippingIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  ContentCopy as CopyIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useApiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const LabelPrinting = () => {
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [shipiumShipmentId, setShipiumShipmentId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [labelResult, setLabelResult] = useState(null);
  const [error, setError] = useState(null);
  const [zplDialogOpen, setZplDialogOpen] = useState(false);
  const [originalPayload, setOriginalPayload] = useState(null); // Store the original request payload from copied shipment
  const [fulfillmentContexts, setFulfillmentContexts] = useState([]);
  const [selectedFulfillmentContextId, setSelectedFulfillmentContextId] = useState('');
  const [loadingFulfillmentContexts, setLoadingFulfillmentContexts] = useState(false);
  const [showSessionExpiredDialog, setShowSessionExpiredDialog] = useState(false);
  const [copiedShipmentId, setCopiedShipmentId] = useState(null);
  
  // Carrier and service method state
  const [carriers, setCarriers] = useState([]);
  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [allCarriersSelected, setAllCarriersSelected] = useState(true);
  const [selectedServiceMethods, setSelectedServiceMethods] = useState([]);
  
  const apiClient = useApiClient();
  const { logout } = useAuth();
  const navigate = useNavigate();

  // Form data state
  const [formData, setFormData] = useState({
    // Sender Information
    senderName: 'Test Sender',
    senderStreet1: '123 Test St',
    senderStreet2: '',
    senderCity: 'Los Angeles',
    senderState: 'CA',
    senderZip: '90210',
    senderCountry: 'US',
    senderPhone: '',
    senderEmail: '',
    
    // Recipient Information
    recipientName: 'John Doe',
    recipientCompany: '',
    recipientStreet1: '6600 N Military Trail',
    recipientStreet2: '',
    recipientCity: 'Boca Raton',
    recipientState: 'FL',
    recipientZip: '33496',
    recipientCountry: 'US',
    recipientPhone: '',
    recipientEmail: '',
    isResidential: false,
    
    // Return Address (Optional)
    useSenderAsReturn: false,
    returnName: '',
    returnCompany: '',
    returnStreet1: '',
    returnStreet2: '',
    returnCity: '',
    returnState: '',
    returnZip: '',
    returnCountry: 'US',
    returnPhone: '',
    returnEmail: '',
    
    // Package Information
    weight: 1.0,
    weightUnit: 'lb',
    length: 10,
    width: 8,
    height: 6,
    dimensionUnit: 'in',
    packagingType: 'box',
    
    // Shipment Options
    serviceType: '',
    includePng: true,
    includePdf: true,
    testMode: true,
    
    // Optional
    productId: 'test-product',
    quantity: 1,
    limitedQuantity: false,
    shipDate: new Date().toISOString().split('T')[0],
    fulfillmentContextIds: [],
    
    // Custom Label Entries
    route: '',
    door: '',
    wave: '',
    vipindicator: '',
    custom1: '',
    custom2: '',
    custom3: '',
    custom4: '',
    custom5: '',
    custom6: '',
    emergencyliteral: '',
    emergency: '',
    hazmat: '',
    dsr: '',
    noair: '',
    skuliteral: '',
    sku: '',
    description: '',
    uomliteral: '',
    uom: '',
    vpcliteral: '',
    vpc: '',
    invliteral: '',
    inv: '',
    barcode: '',
    shipperacctnbr: '',
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCopyShipmentId = async (shipmentId) => {
    try {
      await navigator.clipboard.writeText(shipmentId);
      setCopiedShipmentId(shipmentId);
      showSnackbar('Shipment ID copied to clipboard!', 'success');
      setTimeout(() => setCopiedShipmentId(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      showSnackbar('Failed to copy to clipboard', 'error');
    }
  };

  const handleOpenShipiumShipment = (shipmentId) => {
    const url = `https://partner.shipium.com/veyer-legacy/delivery-reporting/shipment/${shipmentId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // If useSenderAsReturn checkbox is checked, populate return address from sender
      if (field === 'useSenderAsReturn' && value === true) {
        updated.returnName = prev.senderName || '';
        updated.returnCompany = prev.senderName || ''; // Use sender name as company fallback
        updated.returnStreet1 = prev.senderStreet1 || '';
        updated.returnStreet2 = prev.senderStreet2 || '';
        updated.returnCity = prev.senderCity || '';
        updated.returnState = prev.senderState || '';
        updated.returnZip = prev.senderZip || '';
        updated.returnCountry = prev.senderCountry || 'US';
        updated.returnPhone = prev.senderPhone || '';
        updated.returnEmail = prev.senderEmail || '';
      }
      
      // If useSenderAsReturn is checked and sender fields are being updated, sync return address
      if (prev.useSenderAsReturn && field.startsWith('sender')) {
        if (field === 'senderName') {
          updated.returnName = value;
        } else if (field === 'senderStreet1') {
          updated.returnStreet1 = value;
        } else if (field === 'senderStreet2') {
          updated.returnStreet2 = value;
        } else if (field === 'senderCity') {
          updated.returnCity = value;
        } else if (field === 'senderState') {
          updated.returnState = value;
        } else if (field === 'senderZip') {
          updated.returnZip = value;
        } else if (field === 'senderCountry') {
          updated.returnCountry = value;
        } else if (field === 'senderPhone') {
          updated.returnPhone = value;
        } else if (field === 'senderEmail') {
          updated.returnEmail = value;
        }
      }
      
      return updated;
    });
  };

  const handleLookupShipment = async () => {
    if (!shipiumShipmentId.trim()) {
      showSnackbar('Please enter a Shipium Shipment ID', 'warning');
      return;
    }

    setLookupLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get(`/api/v1/labels/shipment/${shipiumShipmentId}`);
      
      // Auto-populate form with shipment data
      if (response.data) {
        const shipment = response.data;
        
        // Store the original request payload for complete copying
        const originalRequestPayload = shipment.requestPayload;
        if (originalRequestPayload) {
          setOriginalPayload(originalRequestPayload);
        }
        
        // Extract custom label entries from the shipment's request payload
        const customLabelEntries = originalRequestPayload?.labelParameters?.customLabelEntries || {};
        
        // Extract fulfillmentContextIds from original request payload
        const originalFulfillmentContextIds = originalRequestPayload?.shipmentParameters?.fulfillmentContextIds || [];
        
        // Extract carrierServiceMethodAllowList from original payload
        const originalCarrierServiceMethodAllowList = originalRequestPayload?.carrierServiceMethodAllowList || [];
        const hasSpecificServiceMethods = originalCarrierServiceMethodAllowList.length > 0;
        
        // Set carrier selection state based on original payload
        setAllCarriersSelected(!hasSpecificServiceMethods);
        setSelectedServiceMethods(hasSpecificServiceMethods ? originalCarrierServiceMethodAllowList : []);
        
        // Extract data from original payload (handling both field name variations)
        const origShipParams = originalRequestPayload?.shipmentParameters || {};
        const origDestAddr = origShipParams.destinationAddress || shipment.destinationAddress || {};
        const origShipFrom = origShipParams.shipFromAddress || shipment.originAddress || {};
        const origReturnTo = origShipParams.returnToAddress || {};
        
        // Populate return address if available
        if (origReturnTo.street1 || origReturnTo.name) {
          setFormData(prev => ({
            ...prev,
            returnName: origReturnTo.name || '',
            returnCompany: origReturnTo.company || origReturnTo.companyName || '',
            returnStreet1: origReturnTo.street1 || '',
            returnStreet2: origReturnTo.street2 || '',
            returnCity: origReturnTo.city || '',
            returnState: origReturnTo.state || '',
            returnZip: origReturnTo.postalCode || '',
            returnCountry: origReturnTo.countryCode || 'US',
            returnPhone: origReturnTo.phoneNumber || origReturnTo.phone || '',
            returnEmail: origReturnTo.emailAddress || origReturnTo.email || '',
          }));
        }
        const origOrderItems = origShipParams.orderItemQuantities || [];
        const origPackaging = origShipParams.packagingType || {};
        const origLabelParams = originalRequestPayload?.labelParameters || {};
        
        setFormData(prev => ({
          ...prev,
          // Map shipment data to form fields
          fulfillmentContextIds: originalFulfillmentContextIds.length > 0 ? originalFulfillmentContextIds : prev.fulfillmentContextIds,
          recipientName: origDestAddr.name || prev.recipientName,
          recipientCompany: origDestAddr.company || origDestAddr.companyName || prev.recipientCompany,
          recipientStreet1: origDestAddr.street1 || prev.recipientStreet1,
          recipientStreet2: origDestAddr.street2 || prev.recipientStreet2,
          recipientCity: origDestAddr.city || prev.recipientCity,
          recipientState: origDestAddr.state || prev.recipientState,
          recipientZip: origDestAddr.postalCode || prev.recipientZip,
          recipientCountry: origDestAddr.countryCode || prev.recipientCountry,
          recipientPhone: origDestAddr.phoneNumber || origDestAddr.phone || prev.recipientPhone,
          recipientEmail: origDestAddr.emailAddress || origDestAddr.email || prev.recipientEmail,
          isResidential: origDestAddr.addressType === 'residential',
          senderName: origShipFrom.name || prev.senderName,
          senderStreet1: origShipFrom.street1 || prev.senderStreet1,
          senderStreet2: origShipFrom.street2 || prev.senderStreet2,
          senderCity: origShipFrom.city || prev.senderCity,
          senderState: origShipFrom.state || prev.senderState,
          senderZip: origShipFrom.postalCode || prev.senderZip,
          senderCountry: origShipFrom.countryCode || prev.senderCountry,
          senderPhone: origShipFrom.phoneNumber || origShipFrom.phone || prev.senderPhone,
          senderEmail: origShipFrom.emailAddress || origShipFrom.email || prev.senderEmail,
          weight: origPackaging.packagingWeight?.weight || origShipParams.totalWeight?.weight || shipment.package?.weight || prev.weight,
          weightUnit: origPackaging.packagingWeight?.weightUnit || origShipParams.totalWeight?.weightUnit || shipment.package?.weightUnit || prev.weightUnit,
          length: origPackaging.linearDimensions?.length || shipment.package?.length || prev.length,
          width: origPackaging.linearDimensions?.width || shipment.package?.width || prev.width,
          height: origPackaging.linearDimensions?.height || shipment.package?.height || prev.height,
          dimensionUnit: origPackaging.linearDimensions?.linearUnit || shipment.package?.dimensionUnit || prev.dimensionUnit,
          packagingType: origPackaging.packagingMaterial || prev.packagingType,
          productId: origOrderItems[0]?.productId || prev.productId,
          quantity: origOrderItems[0]?.quantity || prev.quantity,
          limitedQuantity: origOrderItems[0]?.productDetails?.includes('limited_quantity') || false,
          includePng: origLabelParams.includeLabelImagesInResponse !== undefined ? origLabelParams.includeLabelImagesInResponse : prev.includePng,
          includePdf: origLabelParams.labelFormats?.includes('pdf') || prev.includePdf,
          // Populate custom label entries from request payload
          route: customLabelEntries.route ?? prev.route,
          door: customLabelEntries.door ?? prev.door,
          wave: customLabelEntries.wave ?? prev.wave,
          vipindicator: customLabelEntries.vipindicator ?? prev.vipindicator,
          custom1: customLabelEntries.custom1 ?? prev.custom1,
          custom2: customLabelEntries.custom2 ?? prev.custom2,
          custom3: customLabelEntries.custom3 ?? prev.custom3,
          custom4: customLabelEntries.custom4 ?? prev.custom4,
          custom5: customLabelEntries.custom5 ?? prev.custom5,
          custom6: customLabelEntries.custom6 ?? prev.custom6,
          emergencyliteral: customLabelEntries.emergencyliteral ?? prev.emergencyliteral,
          emergency: customLabelEntries.emergency ?? prev.emergency,
          hazmat: customLabelEntries.hazmat ?? prev.hazmat,
          dsr: customLabelEntries.dsr ?? prev.dsr,
          noair: customLabelEntries.noair ?? prev.noair,
          skuliteral: customLabelEntries.skuliteral ?? prev.skuliteral,
          sku: customLabelEntries.sku ?? prev.sku,
          description: customLabelEntries.description ?? prev.description,
          uomliteral: customLabelEntries.uomliteral ?? prev.uomliteral,
          uom: customLabelEntries.uom ?? prev.uom,
          vpcliteral: customLabelEntries.vpcliteral ?? prev.vpcliteral,
          vpc: customLabelEntries.vpc ?? prev.vpc,
          invliteral: customLabelEntries.invliteral ?? prev.invliteral,
          inv: customLabelEntries.inv ?? prev.inv,
          barcode: customLabelEntries.barcode ?? prev.barcode,
          shipperacctnbr: customLabelEntries.shipperacctnbr ?? prev.shipperacctnbr,
          allCarriers: !hasSpecificServiceMethods,
          selectedServiceMethodIds: hasSpecificServiceMethods ? originalCarrierServiceMethodAllowList : [],
        }));
        showSnackbar('Shipment data loaded successfully!', 'success');
      }
    } catch (err) {
      console.error('Failed to lookup shipment:', err);
      showSnackbar('Failed to load shipment data. Please check the ID and try again.', 'error');
    } finally {
      setLookupLoading(false);
    }
  };

  // Fetch fulfillment contexts on mount and when originalPayload becomes null
  useEffect(() => {
    const fetchFulfillmentContexts = async () => {
      if (originalPayload) {
        // Don't fetch if we're looking up a previous shipment
        return;
      }
      
      setLoadingFulfillmentContexts(true);
      try {
        const response = await apiClient.get('/auth/fulfillment-contexts');
        const contexts = Array.isArray(response.data) ? response.data : [];
        // Filter to only active, non-archived contexts
        const activeContexts = contexts.filter(ctx => ctx.active && !ctx.archived);
        // Sort by name alphabetically
        const sortedContexts = activeContexts.sort((a, b) => {
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.localeCompare(nameB);
        });
        setFulfillmentContexts(sortedContexts);
      } catch (err) {
        console.error('Failed to fetch fulfillment contexts:', err);
        showSnackbar('Failed to load fulfillment contexts', 'error');
      } finally {
        setLoadingFulfillmentContexts(false);
      }
    };

    fetchFulfillmentContexts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalPayload]);

  // Fetch carrier contracts when fulfillment context is selected
  useEffect(() => {
    const fetchCarrierContracts = async () => {
      if (!selectedFulfillmentContextId) {
        // Clear carriers if no fulfillment context is selected
        setCarriers([]);
        setSelectedServiceMethods([]);
        setAllCarriersSelected(true);
        return;
      }

      setLoadingCarriers(true);
      try {
        const response = await apiClient.get(`/auth/carrier-contracts?fulfillmentContextId=${selectedFulfillmentContextId}`);
        console.log('Carrier contracts response:', response);
        console.log('Response data:', response.data);
        const carriersData = response.data?.carriers || [];
        console.log('Carriers data:', carriersData);
        console.log('Number of carriers:', carriersData.length);
        
        carriersData.forEach((carrier, idx) => {
          console.log(`Carrier ${idx + 1}:`, {
            carrierId: carrier.carrierId,
            carrierName: carrier.carrierName,
            serviceMethodsCount: carrier.serviceMethods?.length || 0,
            serviceMethods: carrier.serviceMethods
          });
        });
        
        setCarriers(carriersData);
        // Clear selected service methods when context changes
        setSelectedServiceMethods([]);
        setAllCarriersSelected(true);
      } catch (err) {
        console.error('Failed to fetch carrier contracts:', err);
        console.error('Error details:', err.response?.data || err.message);
        showSnackbar('Failed to load carrier contracts', 'error');
        setCarriers([]);
      } finally {
        setLoadingCarriers(false);
      }
    };

    fetchCarrierContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFulfillmentContextId]);

  // Handle fulfillment context selection
  const handleFulfillmentContextChange = async (event) => {
    const fulfillmentContextId = event.target.value;
    setSelectedFulfillmentContextId(fulfillmentContextId);

    if (!fulfillmentContextId) {
      return;
    }

    try {
      const response = await apiClient.get(`/auth/fulfillment-contexts/${fulfillmentContextId}/details`);
      const details = response.data;

      // Extract origin address information
      const origin = details?.origin;
      if (origin?.address?.address?.location) {
        const location = origin.address.address.location;
        const contact = origin.contact?.contact;

        // Populate sender/origin address fields
        setFormData(prev => ({
          ...prev,
          senderName: contact?.contactName || contact?.name || location.companyName || '',
          senderStreet1: location.address1 || '',
          senderStreet2: location.address2 || '',
          senderCity: location.city || '',
          senderState: location.region || '',
          senderZip: location.postalCode || '',
          senderCountry: location.country || 'US',
          senderPhone: contact?.phoneNumber || '',
          senderEmail: contact?.email || '',
          fulfillmentContextIds: [fulfillmentContextId],
        }));

        showSnackbar('Origin address populated from fulfillment context', 'success');
      }
    } catch (err) {
      console.error('Failed to fetch fulfillment context details:', err);
      showSnackbar('Failed to load fulfillment context details', 'error');
    }
  };

  const handleClearForm = () => {
    setFormData({
      senderName: '',
      senderStreet1: '',
      senderStreet2: '',
      senderCity: '',
      senderState: '',
      senderZip: '',
      senderCountry: 'US',
      senderPhone: '',
      senderEmail: '',
      recipientName: '',
      recipientCompany: '',
      recipientStreet1: '',
      recipientStreet2: '',
      recipientCity: '',
      recipientState: '',
      recipientZip: '',
      recipientCountry: 'US',
      recipientPhone: '',
      recipientEmail: '',
      isResidential: false,
      useSenderAsReturn: false,
      allCarriers: true,
      selectedServiceMethodIds: [],
      returnName: '',
      returnCompany: '',
      returnStreet1: '',
      returnStreet2: '',
      returnCity: '',
      returnState: '',
      returnZip: '',
      returnCountry: 'US',
      returnPhone: '',
      returnEmail: '',
      weight: 1.0,
      weightUnit: 'lb',
      length: 10,
      width: 8,
      height: 6,
      dimensionUnit: 'in',
      packagingType: 'box',
      serviceType: '',
      includePng: true,
      includePdf: true,
      testMode: true,
      productId: 'test-product',
      quantity: 1,
      limitedQuantity: false,
      shipDate: new Date().toISOString().split('T')[0],
      fulfillmentContextIds: [],
      route: '',
      door: '',
      wave: '',
      vipindicator: '',
      custom1: '',
      custom2: '',
      custom3: '',
      custom4: '',
      custom5: '',
      custom6: '',
      emergencyliteral: '',
      emergency: '',
      hazmat: '',
      dsr: '',
      noair: '',
      skuliteral: '',
      sku: '',
      description: '',
      uomliteral: '',
      uom: '',
      vpcliteral: '',
      vpc: '',
      invliteral: '',
      inv: '',
      barcode: '',
      shipperacctnbr: '',
    });
    setShipiumShipmentId('');
    setError(null);
    setLabelResult(null);
    setOriginalPayload(null);
    setSelectedFulfillmentContextId('');
    setAllCarriersSelected(true);
    setSelectedServiceMethods([]);
  };
  
  // Handle "All Carriers" checkbox
  const handleAllCarriersChange = (event) => {
    const checked = event.target.checked;
    setAllCarriersSelected(checked);
    setFormData(prev => ({ ...prev, allCarriers: checked }));
    if (checked) {
      // Clear selected service methods when "All Carriers" is selected
      setSelectedServiceMethods([]);
      setFormData(prev => ({ ...prev, selectedServiceMethodIds: [] }));
    } else {
      // When unchecked, ensure we have at least one service method selected or show the selection UI
      // The user will need to select service methods manually
    }
  };
  
  // Handle service method selection
  const handleServiceMethodChange = (serviceMethodId, checked) => {
    if (checked) {
      setSelectedServiceMethods(prev => [...prev, serviceMethodId]);
      setFormData(prev => ({
        ...prev,
        selectedServiceMethodIds: [...(prev.selectedServiceMethodIds || []), serviceMethodId]
      }));
    } else {
      setSelectedServiceMethods(prev => prev.filter(id => id !== serviceMethodId));
      setFormData(prev => ({
        ...prev,
        selectedServiceMethodIds: (prev.selectedServiceMethodIds || []).filter(id => id !== serviceMethodId)
      }));
    }
  };

  const handleCreateLabel = async () => {
    setLoading(true);
    setError(null);
    setLabelResult(null);

    try {
      // Deep clone the original payload if it exists, otherwise start with a base structure
      const basePayload = originalPayload ? JSON.parse(JSON.stringify(originalPayload)) : {
        currencyCode: 'USD',
        shipmentParameters: {},
        labelParameters: {}
      };

      // Always include ZPL, add PDF if requested
      const labelFormats = ['zpl'];
      if (formData.includePdf) {
        labelFormats.push('pdf');
      }
      if (formData.includePng && !labelFormats.includes('png')) {
        labelFormats.push('png');
      }

      // Update top-level fields
      basePayload.currencyCode = basePayload.currencyCode || 'USD';
      basePayload.generateLabel = basePayload.generateLabel !== undefined ? basePayload.generateLabel : true;
      basePayload.includeEvaluatedServiceMethodsInResponse = basePayload.includeEvaluatedServiceMethodsInResponse || false;
      basePayload.includeLineItemsInEvaluatedServiceMethods = basePayload.includeLineItemsInEvaluatedServiceMethods || false;
      
      // Handle carrier service method selection
      // If "All Carriers" is selected (empty selection or all selected), exclude carrierServiceMethodAllowList
      // Otherwise, include selected service methods in carrierServiceMethodAllowList
      const allServiceMethods = carriers.flatMap(c => c.serviceMethods);
      const hasSelection = selectedServiceMethods.length > 0;
      const allSelected = hasSelection && selectedServiceMethods.length === allServiceMethods.length;
      
      if (allCarriersSelected || !hasSelection || allSelected) {
        // Remove carrierServiceMethodAllowList from payload if it exists (rate shop across all)
        delete basePayload.carrierServiceMethodAllowList;
      } else {
        // Add selected service methods to carrierServiceMethodAllowList
        basePayload.carrierServiceMethodAllowList = selectedServiceMethods;
      }
      
      basePayload.includeInjectionProfiles = basePayload.includeInjectionProfiles !== undefined ? basePayload.includeInjectionProfiles : true;
      basePayload.injectionParameters = basePayload.injectionParameters || [];
      basePayload.simulation = basePayload.simulation || false;
      basePayload.ignoreCarrierMinimumDimensions = basePayload.ignoreCarrierMinimumDimensions || false;

      // Update shipmentParameters
      const shipParams = basePayload.shipmentParameters || {};
      shipParams.testMode = formData.testMode;
      shipParams.orderedDateTime = new Date().toISOString();
      shipParams.shippedDateTime = new Date().toISOString();
      
      // Preserve existing fields from original payload
      if (formData.fulfillmentContextIds && formData.fulfillmentContextIds.length > 0) {
        shipParams.fulfillmentContextIds = formData.fulfillmentContextIds;
      } else if (shipParams.fulfillmentContextIds) {
        // Keep original if form doesn't have it
      }
      
      // Update orderItemQuantities - preserve all fields from original, update productId and quantity
      const origOrderItem = shipParams.orderItemQuantities?.[0] || {};
      
      // Build productDetails array - start with original, add limited_quantity if checked
      const productDetails = [...(origOrderItem.productDetails || [])];
      if (formData.limitedQuantity && !productDetails.includes('limited_quantity')) {
        productDetails.push('limited_quantity');
      } else if (!formData.limitedQuantity) {
        // Remove limited_quantity if unchecked
        const index = productDetails.indexOf('limited_quantity');
        if (index > -1) {
          productDetails.splice(index, 1);
        }
      }
      
      shipParams.orderItemQuantities = [{
        ...origOrderItem,
        productId: formData.productId || origOrderItem.productId,
        quantity: formData.quantity || origOrderItem.quantity || 1,
        productDetails: productDetails.length > 0 ? productDetails : undefined
      }];

      // Update packagingType - preserve original fields, update with form data
      const origPackaging = shipParams.packagingType || {};
      
      // Ensure packagingMaterial is set (required field) - default to 'box' if not provided
      const packagingMaterial = formData.packagingType || origPackaging.packagingMaterial || 'box';
      
      shipParams.packagingType = {
        ...origPackaging,
        packagingMaterial: packagingMaterial,
        packagingSizeName: origPackaging.packagingSizeName || 'custom',
        linearDimensions: {
          ...origPackaging.linearDimensions,
          linearUnit: formData.dimensionUnit || origPackaging.linearDimensions?.linearUnit || 'in',
          length: parseFloat(formData.length) || origPackaging.linearDimensions?.length,
          width: parseFloat(formData.width) || origPackaging.linearDimensions?.width,
          height: parseFloat(formData.height) || origPackaging.linearDimensions?.height
        },
        packagingWeight: {
          ...origPackaging.packagingWeight,
          weight: parseFloat(formData.weight) || origPackaging.packagingWeight?.weight,
          weightUnit: formData.weightUnit || origPackaging.packagingWeight?.weightUnit || 'lb'
        }
      };

      // Update totalWeight
      shipParams.totalWeight = {
        ...shipParams.totalWeight,
        weight: parseFloat(formData.weight) || shipParams.totalWeight?.weight,
        weightUnit: formData.weightUnit || shipParams.totalWeight?.weightUnit || 'lb'
      };

      // Update shipFromAddress - preserve original fields, update with form data
      const origShipFrom = shipParams.shipFromAddress || {};
      shipParams.shipFromAddress = {
        ...origShipFrom,
        name: formData.senderName || origShipFrom.name,
        company: origShipFrom.company || formData.senderName || null, // Preserve original company, fallback to senderName
        street1: formData.senderStreet1 || origShipFrom.street1,
        street2: formData.senderStreet2 || origShipFrom.street2 || null,
        city: formData.senderCity || origShipFrom.city,
        state: formData.senderState || origShipFrom.state,
        postalCode: formData.senderZip || origShipFrom.postalCode,
        countryCode: formData.senderCountry || origShipFrom.countryCode,
        phoneNumber: formData.senderPhone || origShipFrom.phoneNumber || origShipFrom.phone || null,
        phoneNumberCountryCode: origShipFrom.phoneNumberCountryCode || null,
        emailAddress: formData.senderEmail || origShipFrom.emailAddress || origShipFrom.email || null,
        addressType: origShipFrom.addressType || null
      };

      // Update destinationAddress - preserve original fields, update with form data
      const origDest = shipParams.destinationAddress || {};
      shipParams.destinationAddress = {
        ...origDest,
        name: formData.recipientName || origDest.name,
        company: formData.recipientCompany || origDest.company || origDest.companyName || null,
        street1: formData.recipientStreet1 || origDest.street1,
        street2: formData.recipientStreet2 || origDest.street2 || null,
        city: formData.recipientCity || origDest.city,
        state: formData.recipientState || origDest.state,
        postalCode: formData.recipientZip || origDest.postalCode,
        countryCode: formData.recipientCountry || origDest.countryCode,
        phoneNumber: formData.recipientPhone || origDest.phoneNumber || origDest.phone || null,
        phoneNumberCountryCode: origDest.phoneNumberCountryCode || null,
        emailAddress: formData.recipientEmail || origDest.emailAddress || origDest.email || null,
        addressType: formData.isResidential ? 'residential' : 'commercial'
      };

      // Update returnToAddress - only include if at least one field is filled
      const hasReturnAddress = formData.returnStreet1 || formData.returnName || formData.returnCity;
      if (hasReturnAddress) {
        shipParams.returnToAddress = {
          name: formData.returnName || null,
          company: formData.returnCompany || null,
          street1: formData.returnStreet1 || null,
          street2: formData.returnStreet2 || null,
          city: formData.returnCity || null,
          state: formData.returnState || null,
          postalCode: formData.returnZip || null,
          countryCode: formData.returnCountry || 'US',
          phoneNumber: formData.returnPhone || null,
          phoneNumberCountryCode: null, // Can be set if needed
          emailAddress: formData.returnEmail || null,
          addressType: null // Can be set if needed
        };
      } else if (shipParams.returnToAddress) {
        // Preserve original returnToAddress if form doesn't have one
        shipParams.returnToAddress = shipParams.returnToAddress;
      }

      // Preserve other shipmentParameters fields
      if (shipParams.businessDaysOfTransit !== undefined) {
        shipParams.businessDaysOfTransit = shipParams.businessDaysOfTransit;
      }
      if (shipParams.deliverySignatureOption) {
        shipParams.deliverySignatureOption = shipParams.deliverySignatureOption;
      }
      if (shipParams.fulfillmentType) {
        shipParams.fulfillmentType = shipParams.fulfillmentType;
      }

      // Update labelParameters
      const labelParams = basePayload.labelParameters || {};
      labelParams.currencyCode = labelParams.currencyCode || 'USD';
      labelParams.labelFormats = labelFormats;
      labelParams.includeLabelImagesInResponse = formData.includePng;

      // Update customLabelEntries - preserve original, update with form data
      const origCustomEntries = labelParams.customLabelEntries || {};
      labelParams.customLabelEntries = {
        ...origCustomEntries,
        route: formData.route || origCustomEntries.route || '',
        door: formData.door || origCustomEntries.door || '',
        wave: formData.wave || origCustomEntries.wave || '',
        vipindicator: formData.vipindicator || origCustomEntries.vipindicator || '',
        custom1: formData.custom1 || origCustomEntries.custom1 || '',
        custom2: formData.custom2 || origCustomEntries.custom2 || '',
        custom3: formData.custom3 || origCustomEntries.custom3 || '',
        custom4: formData.custom4 || origCustomEntries.custom4 || '',
        custom5: formData.custom5 || origCustomEntries.custom5 || '',
        custom6: formData.custom6 || origCustomEntries.custom6 || '',
        emergencyliteral: formData.emergencyliteral || origCustomEntries.emergencyliteral || '',
        emergency: formData.emergency || origCustomEntries.emergency || '',
        hazmat: formData.hazmat || origCustomEntries.hazmat || '',
        dsr: formData.dsr || origCustomEntries.dsr || '',
        noair: formData.noair || origCustomEntries.noair || '',
        skuliteral: formData.skuliteral || origCustomEntries.skuliteral || '',
        sku: formData.sku || origCustomEntries.sku || '',
        description: formData.description || origCustomEntries.description || '',
        uomliteral: formData.uomliteral || origCustomEntries.uomliteral || '',
        uom: formData.uom || origCustomEntries.uom || '',
        vpcliteral: formData.vpcliteral || origCustomEntries.vpcliteral || '',
        vpc: formData.vpc || origCustomEntries.vpc || '',
        invliteral: formData.invliteral || origCustomEntries.invliteral || '',
        inv: formData.inv || origCustomEntries.inv || '',
        barcode: formData.barcode || origCustomEntries.barcode || '',
        shipperacctnbr: formData.shipperacctnbr || origCustomEntries.shipperacctnbr || ''
      };

      // Preserve other labelParameters fields
      if (labelParams.eligibleForManifest !== undefined) {
        labelParams.eligibleForManifest = labelParams.eligibleForManifest;
      }
      if (labelParams.contentDescription) {
        labelParams.contentDescription = labelParams.contentDescription;
      }

      const requestBody = basePayload;

      console.log('Creating label with request:', requestBody);
      const response = await apiClient.post('/api/v1/labels/create', requestBody, {
        useApiToken: true // Use API token instead of user's login token for Shipium API calls
      });
      
      console.log('Label created successfully:', response.data);
      
      // Map the response to the expected format
      const responseData = response.data;
      const carrierLabel = responseData?.carrierLabel || {};
      const documents = carrierLabel.documents || [];
      
      // Extract file availability from documents
      const hasPng = documents.some(doc => doc.labelFormat === 'png');
      const hasPdf = documents.some(doc => doc.labelFormat === 'pdf');
      const hasZpl = documents.some(doc => doc.labelFormat === 'zpl');
      
      // Extract PNG image if available
      const pngDoc = documents.find(doc => doc.labelFormat === 'png');
      const labelImageBase64 = pngDoc?.labelImage?.imageContents || null;
      
      // Extract ZPL content if available
      const zplDoc = documents.find(doc => doc.labelFormat === 'zpl');
      let zplContent = null;
      
      if (zplDoc) {
        // Check if we have labelContent (plain text) first
        if (zplDoc.labelContent) {
          zplContent = zplDoc.labelContent;
        } 
        // Otherwise, decode base64 from imageContents
        else if (zplDoc.labelImage?.imageContents) {
          try {
            // Decode base64 to get the actual ZPL text
            zplContent = atob(zplDoc.labelImage.imageContents);
          } catch (error) {
            console.error('Failed to decode ZPL content:', error);
            // If decoding fails, try using it as-is (might already be plain text)
            zplContent = zplDoc.labelImage.imageContents;
          }
        }
      }
      
      // Extract carrier selection information
      const carrierSelection = responseData?.carrierSelection || {};
      
      // Extract carrier - check multiple locations
      const carrier = carrierSelection?.carrier || 
                      responseData?.carrier || 
                      carrierLabel?.carrier || 
                      null;
      
      // Extract service method name - check multiple locations
      const service = carrierSelection?.serviceMethodName || 
                      carrierSelection?.carrierServiceMethodId ||
                      responseData?.serviceMethodName ||
                      responseData?.serviceLevel || 
                      responseData?.service ||
                      null;
      
      // Extract cost from response (could be in different places)
      const cost = carrierSelection?.totalCost ||
                   carrierSelection?.carrierCompareCost ||
                   carrierSelection?.carrierInvoiceCost ||
                   carrierSelection?.operationalCost ||
                   responseData?.cost?.amount || 
                   responseData?.totalCost?.amount || 
                   responseData?.carrierLabel?.cost?.amount || 
                   null;
      
      const mappedResult = {
        shipiumShipmentId: responseData?.shipiumShipmentId || responseData?.id,
        trackingNumber: carrierLabel?.carrierTrackingId || responseData?.trackingNumber,
        carrier: carrier,
        service: service,
        cost: cost,
        labelImageBase64: labelImageBase64,
        zplContent: zplContent,
        hasPng: hasPng,
        hasPdf: hasPdf,
        hasZpl: hasZpl,
        // Store full response for debugging
        _fullResponse: responseData
      };
      
      setLabelResult(mappedResult);
      showSnackbar('Label created successfully!', 'success');
      
    } catch (err) {
      console.error('Failed to create label:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      
      // Extract detailed error message
      let errorMessage = 'Failed to create label';
      
      if (err.response) {
        const status = err.response.status;
        const statusText = err.response.statusText;
        const data = err.response.data;
        
        if (status === 401) {
          // Session expired - token refresh likely failed
          errorMessage = 'Your session has expired. Please log in again to continue.';
          // Show dialog to prompt user to log in again
          setShowSessionExpiredDialog(true);
        } else if (status === 403) {
          errorMessage = `Forbidden (403): ${data?.message || 'You do not have permission to perform this action.'}`;
        } else if (status === 400) {
          errorMessage = `Bad Request (400): ${data?.message || 'Invalid request. Please check your input and try again.'}`;
        } else if (status === 500) {
          errorMessage = `Server Error (500): ${data?.message || 'An internal server error occurred. Please try again later.'}`;
        } else {
          errorMessage = `HTTP ${status} ${statusText}: ${data?.message || data?.error || err.message || 'An error occurred'}`;
        }
      } else {
        errorMessage = err.message || 'Failed to create label';
      }
      
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPng = async () => {
    if (!labelResult?.shipiumShipmentId) return;
    
    try {
      const response = await apiClient.get(`/api/v1/labels/gcs/${labelResult.shipiumShipmentId}/png`, {
        headers: { Accept: 'application/octet-stream' }
      });
      
      // response.data should already be a Blob from the API client
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${labelResult.shipiumShipmentId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSnackbar('PNG downloaded successfully!', 'success');
    } catch (err) {
      console.error('Failed to download PNG:', err);
      showSnackbar('Failed to download PNG', 'error');
    }
  };

  const handleDownloadPdf = async () => {
    if (!labelResult?.shipiumShipmentId) return;
    
    try {
      const response = await apiClient.get(`/api/v1/labels/gcs/${labelResult.shipiumShipmentId}/pdf`, {
        headers: { Accept: 'application/octet-stream' }
      });
      
      // response.data should already be a Blob from the API client
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${labelResult.shipiumShipmentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSnackbar('PDF downloaded successfully!', 'success');
    } catch (err) {
      console.error('Failed to download PDF:', err);
      showSnackbar('Failed to download PDF', 'error');
    }
  };

  const handleDownloadZpl = async () => {
    if (!labelResult?.shipiumShipmentId) return;
    
    try {
      const response = await apiClient.get(`/api/v1/labels/gcs/${labelResult.shipiumShipmentId}/zpl`, {
        headers: { Accept: 'application/octet-stream' }
      });
      
      // response.data should already be a Blob from the API client
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${labelResult.shipiumShipmentId}.zpl`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSnackbar('ZPL downloaded successfully!', 'success');
    } catch (err) {
      console.error('Failed to download ZPL:', err);
      showSnackbar('Failed to download ZPL', 'error');
    }
  };

  const handlePrintLabel = async () => {
    console.log('Print label clicked, labelResult:', labelResult);
    
    // If we have base64 image, use it directly
    if (labelResult?.labelImageBase64) {
      try {
        const printWindow = window.open('', '_blank');
        
        if (!printWindow) {
          showSnackbar('Print window was blocked. Please allow popups and try again, or download the PNG and print it manually.', 'warning');
          return;
        }

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Print Label</title>
              <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { 
                  margin: 0; 
                  padding: 20px; 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  min-height: 100vh;
                  background: white;
                }
                img { 
                  max-width: 100%; 
                  height: auto; 
                  display: block;
                }
                @media print { 
                  body { 
                    padding: 0; 
                    margin: 0;
                  }
                  img {
                    width: 100%;
                    page-break-inside: avoid;
                  }
                }
              </style>
            </head>
            <body>
              <img 
                src="data:image/png;base64,${labelResult.labelImageBase64}" 
                alt="Shipping Label"
                onload="setTimeout(function() { window.print(); }, 250);"
                onerror="alert('Failed to load label image for printing'); window.close();"
              />
              <script>
                window.addEventListener('afterprint', function() {
                  setTimeout(function() { window.close(); }, 100);
                });
              </script>
            </body>
          </html>
        `);
        printWindow.document.close();
        
        showSnackbar('Opening print dialog...', 'info');
      } catch (error) {
        console.error('Print error:', error);
        showSnackbar('Failed to open print dialog. Please download the PNG and print it manually.', 'error');
      }
      return;
    }
    
    // Fallback: If no base64 image but PNG is available, download it and open for printing
    if (labelResult?.hasPng && labelResult?.shipiumShipmentId) {
      try {
        showSnackbar('Downloading PNG for printing...', 'info');
        const response = await apiClient.get(`/api/v1/labels/gcs/${labelResult.shipiumShipmentId}/png`, {
          headers: { Accept: 'application/octet-stream' }
        });
        
        const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'image/png' });
        const imageUrl = window.URL.createObjectURL(blob);
        
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          showSnackbar('Print window was blocked. Please allow popups and try again.', 'warning');
          window.URL.revokeObjectURL(imageUrl);
          return;
        }
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Print Label</title>
              <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { 
                  margin: 0; 
                  padding: 20px; 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  min-height: 100vh;
                  background: white;
                }
                img { 
                  max-width: 100%; 
                  height: auto; 
                  display: block;
                }
                @media print { 
                  body { 
                    padding: 0; 
                    margin: 0;
                  }
                  img {
                    width: 100%;
                    page-break-inside: avoid;
                  }
                }
              </style>
            </head>
            <body>
              <img 
                src="${imageUrl}" 
                alt="Shipping Label"
                onload="setTimeout(function() { window.print(); window.URL.revokeObjectURL('${imageUrl}'); }, 250);"
                onerror="alert('Failed to load label image for printing'); window.URL.revokeObjectURL('${imageUrl}'); window.close();"
              />
              <script>
                window.addEventListener('afterprint', function() {
                  window.URL.revokeObjectURL('${imageUrl}');
                  setTimeout(function() { window.close(); }, 100);
                });
              </script>
            </body>
          </html>
        `);
        printWindow.document.close();
        
        showSnackbar('Opening print dialog...', 'info');
      } catch (err) {
        console.error('Failed to print label:', err);
        showSnackbar('Failed to load label for printing. Please download the PNG and print it manually.', 'error');
      }
      return;
    }
    
    // No image available at all
    showSnackbar('No label image available to print. Please ensure PNG format is included when creating the label.', 'warning');
  };

  const handleCopyZpl = () => {
    if (!labelResult?.zplContent) return;
    
    navigator.clipboard.writeText(labelResult.zplContent).then(() => {
      showSnackbar('ZPL copied to clipboard!', 'success');
    }).catch(() => {
      showSnackbar('Failed to copy ZPL', 'error');
    });
  };

  const renderFormFields = () => (
    <Grid container spacing={3}>
      {/* Fulfillment Context Selection - Only show when not looking up a previous shipment */}
      {!originalPayload && (
        <>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShippingIcon color="primary" fontSize="small" />
              <b>Fulfillment Context</b>
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="fulfillment-context-label">Select Fulfillment Context</InputLabel>
              <Select
                labelId="fulfillment-context-label"
                id="fulfillment-context-select"
                value={selectedFulfillmentContextId}
                label="Select Fulfillment Context"
                onChange={handleFulfillmentContextChange}
                disabled={loadingFulfillmentContexts}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {fulfillmentContexts.map((context) => (
                  <MenuItem key={context.fulfillmentContextId} value={context.fulfillmentContextId}>
                    {context.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {loadingFulfillmentContexts && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading fulfillment contexts...
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Carrier Service Method Selection - Only show when fulfillment context is selected */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="carrier-service-method-label">Carrier Service Method Selection</InputLabel>
              <Select
                labelId="carrier-service-method-label"
                id="carrier-service-method-select"
                multiple
                value={selectedServiceMethods || []}
                label="Carrier Service Method Selection"
                onChange={(e) => {
                  console.log('Select onChange triggered:', e.target.value, 'Type:', typeof e.target.value, 'IsArray:', Array.isArray(e.target.value));
                  let newSelection = e.target.value;
                  
                  // Ensure it's an array
                  if (!Array.isArray(newSelection)) {
                    console.warn('Selection is not an array, converting:', newSelection);
                    newSelection = [];
                  }
                  
                  // Handle "All Service Methods" special value
                  if (newSelection.includes('__ALL__')) {
                    const allServiceMethods = carriers.flatMap(c => c.serviceMethods);
                    const allSelected = selectedServiceMethods.length === allServiceMethods.length;
                    if (allSelected) {
                      // Deselect all
                      newSelection = [];
                    } else {
                      // Select all
                      newSelection = allServiceMethods;
                    }
                  } else {
                    // Remove the special "__ALL__" value if it exists
                    newSelection = newSelection.filter(v => v !== '__ALL__');
                  }
                  
                  console.log('Setting selected service methods to:', newSelection);
                  setSelectedServiceMethods(newSelection);
                  setFormData(prev => ({ ...prev, selectedServiceMethodIds: newSelection }));
                  // If all service methods are selected or none are selected, treat as "all carriers"
                  const allServiceMethods = carriers.flatMap(c => c.serviceMethods);
                  setAllCarriersSelected(
                    newSelection.length === 0 || 
                    newSelection.length === allServiceMethods.length
                  );
                }}
                disabled={loadingCarriers || !selectedFulfillmentContextId}
                onOpen={() => console.log('Select opened, current selection:', selectedServiceMethods, 'Carriers:', carriers.length)}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return 'All Carriers (Rate shop across all available carriers and service methods)';
                  }
                  const allServiceMethods = carriers.flatMap(c => c.serviceMethods);
                  if (selected.length === allServiceMethods.length) {
                    return 'All Carriers (Rate shop across all available carriers and service methods)';
                  }
                  // Show selected service methods as chips
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.slice(0, 3).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                      {selected.length > 3 && (
                        <Chip label={`+${selected.length - 3} more`} size="small" />
                      )}
                    </Box>
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                      width: 'auto',
                    },
                  },
                  variant: 'menu',
                  keepMounted: false,
                  autoFocus: false,
                }}
              >
                {loadingCarriers && selectedFulfillmentContextId && (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <Typography variant="body2">Loading carrier contracts...</Typography>
                    </Box>
                  </MenuItem>
                )}
                {!selectedFulfillmentContextId && (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Please select a fulfillment context first
                    </Typography>
                  </MenuItem>
                )}
                {!loadingCarriers && selectedFulfillmentContextId && carriers.length > 0 && (
                  <>
                    {/* All Service Methods option */}
                    <MenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const allServiceMethods = carriers.flatMap(c => c.serviceMethods);
                        const allSelected = selectedServiceMethods.length === allServiceMethods.length;
                        if (allSelected) {
                          // Deselect all
                          setSelectedServiceMethods([]);
                          setFormData(prev => ({ ...prev, selectedServiceMethodIds: [] }));
                          setAllCarriersSelected(true);
                        } else {
                          // Select all
                          setSelectedServiceMethods(allServiceMethods);
                          setFormData(prev => ({ ...prev, selectedServiceMethodIds: allServiceMethods }));
                          setAllCarriersSelected(true);
                        }
                      }}
                      onMouseDown={(e) => {
                        // Prevent the Select from closing
                        e.preventDefault();
                      }}
                      sx={{ 
                        fontWeight: 'bold', 
                        bgcolor: 'action.selected',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        mb: 0.5,
                        pointerEvents: 'auto'
                      }}
                    >
                      <Checkbox
                        checked={selectedServiceMethods.length === carriers.flatMap(c => c.serviceMethods).length && selectedServiceMethods.length > 0}
                        indeterminate={selectedServiceMethods.length > 0 && selectedServiceMethods.length < carriers.flatMap(c => c.serviceMethods).length}
                        sx={{ mr: 1, pointerEvents: 'none' }}
                      />
                      All Service Methods
                    </MenuItem>
                    <Divider sx={{ my: 0.5 }} />
                    {carriers.map((carrier) => (
                      <React.Fragment key={carrier.carrierId}>
                        <MenuItem 
                          disabled 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: 'primary.main',
                            opacity: 1,
                            pt: 1.5,
                            pb: 0.5
                          }}
                        >
                          {carrier.carrierName} ({carrier.carrierId})
                        </MenuItem>
                        {carrier.serviceMethods.map((serviceMethodId) => {
                          const isSelected = selectedServiceMethods.includes(serviceMethodId);
                          return (
                            <MenuItem 
                              key={`${carrier.carrierId}-${serviceMethodId}`} 
                              value={serviceMethodId}
                              sx={{ pl: 4 }}
                            >
                              <Checkbox
                                checked={isSelected}
                                sx={{ mr: 1 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Toggle selection
                                  const newSelection = isSelected
                                    ? selectedServiceMethods.filter(id => id !== serviceMethodId)
                                    : [...selectedServiceMethods, serviceMethodId];
                                  
                                  setSelectedServiceMethods(newSelection);
                                  setFormData(prev => ({ ...prev, selectedServiceMethodIds: newSelection }));
                                  
                                  const allServiceMethods = carriers.flatMap(c => c.serviceMethods);
                                  setAllCarriersSelected(
                                    newSelection.length === 0 || 
                                    newSelection.length === allServiceMethods.length
                                  );
                                }}
                              />
                              {serviceMethodId}
                            </MenuItem>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </>
                )}
                {!loadingCarriers && selectedFulfillmentContextId && carriers.length === 0 && (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No service methods available
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          {!allCarriersSelected && selectedFulfillmentContextId && carriers.length === 0 && !loadingCarriers && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No carrier contracts available for this fulfillment context.
              </Typography>
            </Grid>
          )}
        </>
      )}

      {/* Sender Information */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShippingIcon color="primary" fontSize="small" />
          <b>Sender Information</b>
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Sender Name"
          value={formData.senderName}
          onChange={(e) => handleInputChange('senderName', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Phone (Optional)"
          value={formData.senderPhone}
          onChange={(e) => handleInputChange('senderPhone', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          label="Street Address"
          value={formData.senderStreet1}
          onChange={(e) => handleInputChange('senderStreet1', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Apt/Suite (Optional)"
          value={formData.senderStreet2}
          onChange={(e) => handleInputChange('senderStreet2', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="City"
          value={formData.senderCity}
          onChange={(e) => handleInputChange('senderCity', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="State"
          value={formData.senderState}
          onChange={(e) => handleInputChange('senderState', e.target.value)}
          required
          inputProps={{ maxLength: 2 }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="ZIP Code"
          value={formData.senderZip}
          onChange={(e) => handleInputChange('senderZip', e.target.value)}
          required
        />
      </Grid>

      {/* Recipient Information */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShippingIcon color="secondary" fontSize="small" />
          <b>Recipient Information</b>
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Recipient Name"
          value={formData.recipientName}
          onChange={(e) => handleInputChange('recipientName', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Company (Optional)"
          value={formData.recipientCompany}
          onChange={(e) => handleInputChange('recipientCompany', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          label="Street Address"
          value={formData.recipientStreet1}
          onChange={(e) => handleInputChange('recipientStreet1', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Apt/Suite (Optional)"
          value={formData.recipientStreet2}
          onChange={(e) => handleInputChange('recipientStreet2', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="City"
          value={formData.recipientCity}
          onChange={(e) => handleInputChange('recipientCity', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="State"
          value={formData.recipientState}
          onChange={(e) => handleInputChange('recipientState', e.target.value)}
          required
          inputProps={{ maxLength: 2 }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="ZIP Code"
          value={formData.recipientZip}
          onChange={(e) => handleInputChange('recipientZip', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Phone Number"
          value={formData.recipientPhone}
          onChange={(e) => handleInputChange('recipientPhone', e.target.value)}
          placeholder="Optional but required by some carriers"
          helperText="Required by some carriers (e.g., FedEx)"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={formData.recipientEmail}
          onChange={(e) => handleInputChange('recipientEmail', e.target.value)}
          placeholder="Optional but required by some carriers"
          helperText="Required by some carriers (e.g., FedEx)"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.isResidential}
              onChange={(e) => handleInputChange('isResidential', e.target.checked)}
              color="primary"
            />
          }
          label="Residential Address"
        />
      </Grid>

      {/* Return Address (Optional) */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShippingIcon color="secondary" fontSize="small" />
          Return Address (Optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          If provided, packages will be returned to this address. Leave blank to use default return address.
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.useSenderAsReturn}
              onChange={(e) => handleInputChange('useSenderAsReturn', e.target.checked)}
              color="primary"
            />
          }
          label="Use Sender Address as Return Address"
        />
        {formData.useSenderAsReturn && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: 0.5 }}>
            Return address fields will be populated from sender information. You can still override any field below.
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Return Name"
          value={formData.returnName}
          onChange={(e) => handleInputChange('returnName', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Company (Optional)"
          value={formData.returnCompany}
          onChange={(e) => handleInputChange('returnCompany', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          label="Street Address"
          value={formData.returnStreet1}
          onChange={(e) => handleInputChange('returnStreet1', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Apt/Suite (Optional)"
          value={formData.returnStreet2}
          onChange={(e) => handleInputChange('returnStreet2', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="City"
          value={formData.returnCity}
          onChange={(e) => handleInputChange('returnCity', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="State"
          value={formData.returnState}
          onChange={(e) => handleInputChange('returnState', e.target.value)}
          inputProps={{ maxLength: 2 }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="ZIP Code"
          value={formData.returnZip}
          onChange={(e) => handleInputChange('returnZip', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Country"
          value={formData.returnCountry}
          onChange={(e) => handleInputChange('returnCountry', e.target.value)}
          inputProps={{ maxLength: 2 }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Phone Number"
          value={formData.returnPhone}
          onChange={(e) => handleInputChange('returnPhone', e.target.value)}
          placeholder="Accepts vanity phone numbers (e.g., 1-800-FLOWERS)"
          helperText="Vanity phone numbers accepted"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Email Address (Optional)"
          type="email"
          value={formData.returnEmail}
          onChange={(e) => handleInputChange('returnEmail', e.target.value)}
        />
      </Grid>

      {/* Order Item Information */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          <b>Order Item Information</b>
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Product ID"
          value={formData.productId}
          onChange={(e) => handleInputChange('productId', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Quantity"
          type="number"
          value={formData.quantity}
          onChange={(e) => handleInputChange('quantity', parseInt(e.target.value) || 1)}
          required
          inputProps={{ min: 1, step: 1 }}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.limitedQuantity}
              onChange={(e) => handleInputChange('limitedQuantity', e.target.checked)}
            />
          }
          label="Limited Quantity (adds 'limited_quantity' to productDetails)"
        />
      </Grid>

      {/* Package Information */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          <b>Package Information</b>
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Weight"
          type="number"
          value={formData.weight}
          onChange={(e) => handleInputChange('weight', e.target.value)}
          required
          inputProps={{ min: 0.1, step: 0.1 }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControl fullWidth>
          <InputLabel>Weight Unit</InputLabel>
          <Select
            value={formData.weightUnit}
            onChange={(e) => handleInputChange('weightUnit', e.target.value)}
            label="Weight Unit"
          >
            <MenuItem value="lb">Pounds (lb)</MenuItem>
            <MenuItem value="kg">Kilograms (kg)</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={2}>
        <TextField
          fullWidth
          label="Length"
          type="number"
          value={formData.length}
          onChange={(e) => handleInputChange('length', e.target.value)}
          required
          inputProps={{ min: 1, step: 1 }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <TextField
          fullWidth
          label="Width"
          type="number"
          value={formData.width}
          onChange={(e) => handleInputChange('width', e.target.value)}
          required
          inputProps={{ min: 1, step: 1 }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <TextField
          fullWidth
          label="Height"
          type="number"
          value={formData.height}
          onChange={(e) => handleInputChange('height', e.target.value)}
          required
          inputProps={{ min: 1, step: 1 }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth required>
          <InputLabel>Packaging Material *</InputLabel>
          <Select
            value={formData.packagingType}
            onChange={(e) => handleInputChange('packagingType', e.target.value)}
            label="Packaging Material *"
          >
            <MenuItem value="box">Box</MenuItem>
            <MenuItem value="envelope">Envelope</MenuItem>
            <MenuItem value="flat_pack">Flat Pack</MenuItem>
            <MenuItem value="fedex_one_rate_envelope">FedEx One Rate Envelope</MenuItem>
            <MenuItem value="fedex_one_rate_pak">FedEx One Rate Pak</MenuItem>
            <MenuItem value="fedex_one_rate_small_box">FedEx One Rate Small Box</MenuItem>
            <MenuItem value="fedex_one_rate_medium_box">FedEx One Rate Medium Box</MenuItem>
            <MenuItem value="fedex_one_rate_large_box">FedEx One Rate Large Box</MenuItem>
            <MenuItem value="fedex_one_rate_extra_large_box">FedEx One Rate Extra Large Box</MenuItem>
            <MenuItem value="fedex_one_rate_tube">FedEx One Rate Tube</MenuItem>
            <MenuItem value="fedex_ten_kg_box">FedEx 10kg Box</MenuItem>
            <MenuItem value="fedex_twenty_five_kg_box">FedEx 25kg Box</MenuItem>
            <MenuItem value="mailing_tube">Mailing Tube</MenuItem>
            <MenuItem value="parcel_pallet">Parcel Pallet</MenuItem>
            <MenuItem value="small_tire">Small Tire</MenuItem>
            <MenuItem value="large_tire">Large Tire</MenuItem>
            <MenuItem value="usps_large_box">USPS Large Box</MenuItem>
            <MenuItem value="usps_large_box_to_apo_fpo_dpo">USPS Large Box to APO/FPО/DPO</MenuItem>
            <MenuItem value="usps_medium_box">USPS Medium Box</MenuItem>
            <MenuItem value="usps_small_box">USPS Small Box</MenuItem>
            <MenuItem value="usps_legal_envelope">USPS Legal Envelope</MenuItem>
            <MenuItem value="usps_envelope">USPS Envelope</MenuItem>
            <MenuItem value="usps_padded_envelope">USPS Padded Envelope</MenuItem>
            <MenuItem value="ups_express_envelope">UPS Express Envelope</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Label Options */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          <b>Label Options</b>
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Label format: <strong>ZPL (Zebra Printer)</strong> - Always included
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Additional Formats</InputLabel>
          <Select
            multiple
            value={[formData.includePng && 'png', formData.includePdf && 'pdf'].filter(Boolean)}
            onChange={(e) => {
              const values = e.target.value;
              handleInputChange('includePng', values.includes('png'));
              handleInputChange('includePdf', values.includes('pdf'));
            }}
            label="Additional Formats"
          >
            <MenuItem value="png">Include PNG Image</MenuItem>
            <MenuItem value="pdf">Include PDF</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.testMode}
              onChange={(e) => handleInputChange('testMode', e.target.checked)}
            />
          }
          label="Test Mode"
        />
      </Grid>

      {/* Custom Label Entries */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          <b>Custom Label Entries</b> (Unique to ODP)
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Route"
          value={formData.route}
          onChange={(e) => handleInputChange('route', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Door"
          value={formData.door}
          onChange={(e) => handleInputChange('door', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Wave"
          value={formData.wave}
          onChange={(e) => handleInputChange('wave', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="VIP Indicator"
          value={formData.vipindicator}
          onChange={(e) => handleInputChange('vipindicator', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="No Air"
          value={formData.noair}
          onChange={(e) => handleInputChange('noair', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Custom 1"
          value={formData.custom1}
          onChange={(e) => handleInputChange('custom1', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Custom 2"
          value={formData.custom2}
          onChange={(e) => handleInputChange('custom2', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Custom 3"
          value={formData.custom3}
          onChange={(e) => handleInputChange('custom3', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Custom 4"
          value={formData.custom4}
          onChange={(e) => handleInputChange('custom4', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Custom 5"
          value={formData.custom5}
          onChange={(e) => handleInputChange('custom5', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Custom 6"
          value={formData.custom6}
          onChange={(e) => handleInputChange('custom6', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Emergency Literal"
          value={formData.emergencyliteral}
          onChange={(e) => handleInputChange('emergencyliteral', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Emergency"
          value={formData.emergency}
          onChange={(e) => handleInputChange('emergency', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Hazmat"
          value={formData.hazmat}
          onChange={(e) => handleInputChange('hazmat', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="DSR"
          value={formData.dsr}
          onChange={(e) => handleInputChange('dsr', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="SKU Literal"
          value={formData.skuliteral}
          onChange={(e) => handleInputChange('skuliteral', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          label="SKU"
          value={formData.sku}
          onChange={(e) => handleInputChange('sku', e.target.value)}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Description"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="UOM Literal"
          value={formData.uomliteral}
          onChange={(e) => handleInputChange('uomliteral', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="UOM"
          value={formData.uom}
          onChange={(e) => handleInputChange('uom', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="VPC Literal"
          value={formData.vpcliteral}
          onChange={(e) => handleInputChange('vpcliteral', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="VPC"
          value={formData.vpc}
          onChange={(e) => handleInputChange('vpc', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="INV Literal"
          value={formData.invliteral}
          onChange={(e) => handleInputChange('invliteral', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          label="INV"
          value={formData.inv}
          onChange={(e) => handleInputChange('inv', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Barcode"
          value={formData.barcode}
          onChange={(e) => handleInputChange('barcode', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Shipper Account Number"
          value={formData.shipperacctnbr}
          onChange={(e) => handleInputChange('shipperacctnbr', e.target.value)}
        />
      </Grid>

      {/* Action Buttons */}
      <Grid item xs={12} sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleClearForm}
          startIcon={<ClearIcon />}
          disabled={loading}
        >
          Clear Form
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateLabel}
          startIcon={loading ? <CircularProgress size={20} /> : <PrintIcon />}
          disabled={loading}
          size="large"
        >
          {loading ? 'Creating Label...' : 'Create Label'}
        </Button>
      </Grid>
    </Grid>
  );

  const renderLabelResult = () => {
    if (!labelResult) return null;

    return (
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'success.50', border: '2px solid', borderColor: 'success.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <SuccessIcon color="success" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h5" color="success.dark">
              Label Created Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Shipment ID: {labelResult.shipiumShipmentId || 'N/A'}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Label Details */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Label Details</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box>
                    <strong>Shipment ID:</strong>{' '}
                    {labelResult.shipiumShipmentId ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                        <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
                          {labelResult.shipiumShipmentId}
                        </Typography>
                        <Tooltip title={copiedShipmentId === labelResult.shipiumShipmentId ? 'Copied!' : 'Copy Shipment ID'}>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyShipmentId(labelResult.shipiumShipmentId)}
                            sx={{ 
                              opacity: 0.7,
                              '&:hover': { opacity: 1 },
                              color: copiedShipmentId === labelResult.shipiumShipmentId ? 'success.main' : 'inherit'
                            }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open in Shipium">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenShipiumShipment(labelResult.shipiumShipmentId)}
                            sx={{ 
                              opacity: 0.7,
                              '&:hover': { opacity: 1 }
                            }}
                          >
                            <UploadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      ' N/A'
                    )}
                  </Box>
                  <Box><strong>Tracking Number:</strong> {labelResult.trackingNumber || 'N/A'}</Box>
                  <Box><strong>Carrier:</strong> {labelResult.carrier || 'N/A'}</Box>
                  <Box><strong>Service:</strong> {labelResult.service || 'N/A'}</Box>
                  <Box><strong>Cost:</strong> {labelResult.cost ? `$${typeof labelResult.cost === 'number' ? labelResult.cost.toFixed(2) : labelResult.cost}` : 'N/A'}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Download Actions */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Download & Print</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {labelResult.labelImageBase64 && (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={handlePrintLabel}
                        fullWidth
                      >
                        Print Label
                      </Button>
                    </>
                  )}
                  {labelResult.hasPng && (
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadPng}
                      fullWidth
                    >
                      Download PNG
                    </Button>
                  )}
                  {labelResult.hasPdf && (
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadPdf}
                      fullWidth
                    >
                      Download PDF
                    </Button>
                  )}
                  {labelResult.hasZpl && (
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadZpl}
                      fullWidth
                    >
                      Download ZPL
                    </Button>
                  )}
                  {labelResult.zplContent && (
                    <Button
                      variant="outlined"
                      startIcon={<CopyIcon />}
                      onClick={() => setZplDialogOpen(true)}
                      fullWidth
                    >
                      View ZPL Code
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Label Image Preview */}
          {labelResult.labelImageBase64 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Label Preview</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <img
                      src={`data:image/png;base64,${labelResult.labelImageBase64}`}
                      alt="Shipping Label"
                      style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ccc' }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  const renderError = () => {
    if (!error) return null;

    return (
      <Alert 
        severity="error" 
        sx={{ mt: 3, mb: 3 }} 
        onClose={() => setError(null)}
      >
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
          Error Creating Label
        </Typography>
        <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
          {error}
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          Please review the form and try again. If this is an authentication error, please try logging out and logging back in.
        </Typography>
      </Alert>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PrintIcon color="primary" />
        Label Printing
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a new shipping label by filling out the form below. You can optionally lookup an existing shipment to auto-populate the form.
          </Typography>
          
          {/* Optional Shipment Lookup */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon fontSize="small" />
              Auto-Populate from Existing Shipment (Optional)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Shipium Shipment ID"
                value={shipiumShipmentId}
                onChange={(e) => setShipiumShipmentId(e.target.value)}
                placeholder="e.g., 123e4567-e89b-12d3-a456-426614174000"
                disabled={lookupLoading}
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleLookupShipment}
                startIcon={lookupLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                disabled={lookupLoading || !shipiumShipmentId.trim()}
                sx={{ minWidth: 120 }}
              >
                {lookupLoading ? 'Loading...' : 'Lookup'}
              </Button>
            </Box>
          </Paper>

          <Divider sx={{ my: 3 }} />

          {renderFormFields()}
        </CardContent>
      </Card>

      {renderError()}
      {renderLabelResult()}

      {/* ZPL Dialog */}
      <Dialog open={zplDialogOpen} onClose={() => setZplDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          ZPL Code
          <IconButton
            onClick={handleCopyZpl}
            sx={{ position: 'absolute', right: 48, top: 8 }}
            size="small"
          >
            <Tooltip title="Copy to clipboard">
              <CopyIcon />
            </Tooltip>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Paper sx={{ 
            p: 2, 
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            maxHeight: 400, 
            overflow: 'auto' 
          }}>
            <Typography 
              component="pre" 
              sx={{ 
                fontFamily: 'monospace', 
                fontSize: '0.875rem', 
                whiteSpace: 'pre-wrap', 
                wordBreak: 'break-all',
                color: 'text.primary',
                backgroundColor: 'background.default',
                padding: 2,
                borderRadius: 1,
                margin: 0
              }}
            >
              {labelResult?.zplContent || 'No ZPL content available'}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZplDialogOpen(false)}>Close</Button>
          <Button onClick={handleDownloadZpl} startIcon={<DownloadIcon />} variant="contained">
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Session Expired Dialog */}
      <Dialog
        open={showSessionExpiredDialog}
        onClose={() => setShowSessionExpiredDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          Session Expired
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Your session has expired. The system attempted to refresh your authentication token, but it was unsuccessful.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please log in again to continue creating labels.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSessionExpiredDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              logout();
              navigate('/login');
            }}
            variant="contained"
            color="primary"
          >
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LabelPrinting;

